import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import LockResetIcon from '@mui/icons-material/LockReset'
import { supabase } from '../../config/supabase'
import { ENV } from '../../config/env'

interface User {
  id: string
  email: string
  created_at: string
  is_admin?: boolean
  undertaker_count?: number
}

interface Undertaker {
  id: string
  name: string
  city: string
  address: string
}

interface Chain {
  id: string
  name: string
  slug: string
}

interface UserUndertaker {
  id: string
  undertaker_id?: string
  chain_id?: string
  undertaker_name?: string
  undertaker_city?: string
  undertaker_address?: string
  chain_name?: string
  chain_location_count?: number
  claim_type: 'location' | 'chain'
}

export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([])
  const [filteredUsers, setFilteredUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Create User Dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [newUserEmail, setNewUserEmail] = useState('')
  const [newUserPassword, setNewUserPassword] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [creating, setCreating] = useState(false)

  // Manage Undertakers Dialog
  const [manageDialogOpen, setManageDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [userUndertakers, setUserUndertakers] = useState<UserUndertaker[]>([])
  const [allUndertakers, setAllUndertakers] = useState<Undertaker[]>([])
  const [allChains, setAllChains] = useState<Chain[]>([])
  const [selectedUndertaker, setSelectedUndertaker] = useState<Undertaker | null>(null)
  const [selectedChain, setSelectedChain] = useState<Chain | null>(null)
  const [managingUndertakers, setManagingUndertakers] = useState(false)
  const [claimType, setClaimType] = useState<'location' | 'chain'>('location')

  // Reset Password Dialog
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false)
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [resettingPassword, setResettingPassword] = useState(false)

  useEffect(() => {
    fetchUsers()
    fetchAllUndertakers()
    fetchAllChains()
  }, [])

  useEffect(() => {
    filterUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, searchTerm])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)

      // Get current user's session token
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      // Call Edge Function to list users (requires admin)
      const response = await fetch(
        `${ENV.SUPABASE_URL}/functions/v1/list-users`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': ENV.SUPABASE_ANON_KEY,
          },
        }
      )

      if (!response.ok) {
        const errorText = await response.text()
        console.error('List users response:', response.status, errorText)
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }

      const result = await response.json()
      console.log('List users result:', result)
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch users')
      }

      setUsers(result.users || [])
    } catch (err: any) {
      setError(err.message)
      console.error('Error fetching users:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchAllUndertakers = async () => {
    try {
      const { data, error } = await supabase
        .from('undertakers')
        .select('id, name, city, address')
        .order('name')

      if (error) throw error
      setAllUndertakers(data || [])
    } catch (err) {
      console.error('Error fetching undertakers:', err)
    }
  }

  const fetchAllChains = async () => {
    try {
      const { data, error } = await supabase
        .from('chains')
        .select('id, name, slug')
        .order('name')

      if (error) throw error
      setAllChains(data || [])
    } catch (err) {
      console.error('Error fetching chains:', err)
    }
  }

  const filterUsers = () => {
    if (!searchTerm) {
      setFilteredUsers(users)
      return
    }

    const search = searchTerm.toLowerCase()
    const filtered = users.filter(u => u.email.toLowerCase().includes(search))
    setFilteredUsers(filtered)
  }

  const handleCreateUser = async () => {
    if (!newUserEmail || !newUserPassword) {
      setError('Udfyld venligst email og adgangskode')
      return
    }

    try {
      setCreating(true)
      setError(null)
      setSuccess(null)

      // Get current user's session token
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      // Call Edge Function to create user (requires admin)
      console.log('Creating user with:', { email: newUserEmail, is_admin: isAdmin })
      
      const response = await fetch(
        `${ENV.SUPABASE_URL}/functions/v1/create-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': ENV.SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            email: newUserEmail,
            password: newUserPassword,
            is_admin: isAdmin,
          }),
        }
      )
      
      console.log('Response status:', response.status, response.statusText)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Create user response:', response.status, errorText)
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }

      const result = await response.json()
      console.log('Create user result:', result)
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to create user')
      }

      setCreateDialogOpen(false)
      setNewUserEmail('')
      setNewUserPassword('')
      setIsAdmin(false)
      
      // Refresh users list
      await fetchUsers()
      
      setSuccess(`Bruger oprettet! Email: ${newUserEmail}`)
    } catch (err: any) {
      setError(err.message)
      console.error('Error creating user:', err)
    } finally {
      setCreating(false)
    }
  }

  const handleManageUndertakers = async (user: User) => {
    setSelectedUser(user)
    setManageDialogOpen(true)
    setError(null)
    setSuccess(null)
    await fetchUserUndertakers(user.id)
  }

  const fetchUserUndertakers = async (userId: string) => {
    try {
      console.log('Fetching claims for user:', userId)
      
      const { data, error } = await supabase
        .from('claims')
        .select(`
          id,
          undertaker_id,
          chain_id,
          undertakers (name, city, address),
          chains (name, slug)
        `)
        .eq('user_id', userId)
        .eq('status', 'approved')

      if (error) throw error

      console.log('Fetched claims data:', data)

      // Format claims - handle both location and chain claims
      const formatted: UserUndertaker[] = await Promise.all((data || []).map(async (item: any) => {
        if (item.chain_id) {
          // Chain claim - count locations
          const { count } = await supabase
            .from('undertakers')
            .select('*', { count: 'exact', head: true })
            .eq('chain_id', item.chain_id)
          
          return {
            id: item.id,
            chain_id: item.chain_id,
            chain_name: item.chains?.name || 'Unknown Chain',
            chain_location_count: count || 0,
            claim_type: 'chain' as const,
          }
        } else {
          // Location claim
          return {
            id: item.id,
            undertaker_id: item.undertaker_id,
            undertaker_name: item.undertakers?.name || 'Unknown',
            undertaker_city: item.undertakers?.city || '',
            undertaker_address: item.undertakers?.address || '',
            claim_type: 'location' as const,
          }
        }
      }))

      console.log('Formatted claims:', formatted)
      setUserUndertakers(formatted)
    } catch (err) {
      console.error('Error fetching user claims:', err)
      setError('Kunne ikke hente claims: ' + (err as any).message)
    }
  }

  const handleAddUndertaker = async () => {
    if (!selectedUser || (!selectedUndertaker && !selectedChain)) return

    try {
      setManagingUndertakers(true)
      setError(null)

      // Create auto-approved claim
      const claimData: any = {
        user_id: selectedUser.id,
        status: 'approved',
        reviewed_at: new Date().toISOString(),
        notes: 'Tildelt direkte af admin',
      }

      if (claimType === 'chain' && selectedChain) {
        claimData.chain_id = selectedChain.id
      } else if (claimType === 'location' && selectedUndertaker) {
        claimData.undertaker_id = selectedUndertaker.id
      } else {
        setError('Vælg en kæde eller lokation')
        setManagingUndertakers(false)
        return
      }

      const { error } = await supabase
        .from('claims')
        .insert([claimData])

      if (error) {
        // Check if it's a unique constraint error
        if (error.message.includes('duplicate') || error.code === '23505') {
          setError(`Denne ${claimType === 'chain' ? 'kæde' : 'lokation'} er allerede tildelt til brugeren`)
        } else {
          throw error
        }
        return
      }

      // Refresh the list
      await fetchUserUndertakers(selectedUser.id)
      setSelectedUndertaker(null)
      setSelectedChain(null)
      
      setSuccess(`${claimType === 'chain' ? 'Kæde' : 'Lokation'} tildelt!`)
      setTimeout(() => setSuccess(null), 2000)
    } catch (err: any) {
      console.error('Error adding claim:', err)
      setError('Fejl: ' + err.message)
    } finally {
      setManagingUndertakers(false)
    }
  }

  const handleRemoveUndertaker = async (claimId: string) => {
    try {
      console.log('Removing claim:', claimId)
      
      const { error } = await supabase
        .from('claims')
        .delete()
        .eq('id', claimId)

      if (error) throw error

      // Refresh the list
      if (selectedUser) {
        await fetchUserUndertakers(selectedUser.id)
      }
      
      setSuccess('Bedemand fjernet!')
      
      // Auto-hide success after 2 seconds
      setTimeout(() => setSuccess(null), 2000)
    } catch (err: any) {
      console.error('Error removing undertaker:', err)
      setError('Fejl ved fjernelse: ' + err.message)
    }
  }

  const handleDeleteUser = async (user: User) => {
    const confirmMessage = `Er du sikker på at du vil slette brugeren "${user.email}"?\n\nDette vil også fjerne alle deres tilknytninger til bedemænd.`
    
    if (!window.confirm(confirmMessage)) {
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Get current user's session token
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      // Call Edge Function to delete user
      const response = await fetch(
        `${ENV.SUPABASE_URL}/functions/v1/delete-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': ENV.SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            user_id: user.id,
          }),
        }
      )

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Delete user response:', response.status, errorText)
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }

      const result = await response.json()
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete user')
      }

      // Refresh users list
      await fetchUsers()
      
      setSuccess(`Bruger ${user.email} slettet!`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError('Fejl ved sletning: ' + err.message)
      console.error('Error deleting user:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleResetPasswordClick = (user: User) => {
    setResetPasswordUser(user)
    setNewPassword('')
    setResetPasswordDialogOpen(true)
    setError(null)
    setSuccess(null)
  }

  const handleResetPassword = async () => {
    if (!resetPasswordUser || !newPassword) {
      setError('Adgangskode er påkrævet')
      return
    }

    if (newPassword.length < 6) {
      setError('Adgangskoden skal være mindst 6 tegn')
      return
    }

    try {
      setResettingPassword(true)
      setError(null)

      // Get current user's session token
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      // Call Edge Function to reset password
      const response = await fetch(
        `${ENV.SUPABASE_URL}/functions/v1/reset-user-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': ENV.SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            user_id: resetPasswordUser.id,
            new_password: newPassword,
          }),
        }
      )

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Reset password response:', response.status, errorText)
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }

      const result = await response.json()
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to reset password')
      }

      setResetPasswordDialogOpen(false)
      setNewPassword('')
      setResetPasswordUser(null)
      
      setSuccess(`Adgangskode ændret for ${resetPasswordUser.email}!`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError('Fejl ved ændring af adgangskode: ' + err.message)
      console.error('Error resetting password:', err)
    } finally {
      setResettingPassword(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Administrer brugere ({filteredUsers.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Opret bruger
        </Button>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Søg på email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {filteredUsers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Ingen brugere fundet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchTerm ? 'Prøv en anden søgning' : 'Opret den første bruger'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Bedemænd</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Oprettet</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }} align="right">Handling</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {user.email}
                      </Typography>
                      {user.is_admin && (
                        <Chip label="Admin" size="small" color="success" sx={{ mt: 0.5 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user.undertaker_count && user.undertaker_count > 0 ? (
                      <Chip
                        label={`${user.undertaker_count} bedemænd`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString('da-DK')}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleManageUndertakers(user)}
                      title="Administrer bedemænd"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={() => handleResetPasswordClick(user)}
                      title="Nulstil adgangskode"
                    >
                      <LockResetIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteUser(user)}
                      title="Slet bruger"
                      disabled={user.is_admin}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Opret ny bruger</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Midlertidig Adgangskode"
              type="password"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              sx={{ mb: 2 }}
              helperText="Minimum 6 tegn. Brugeren kan ændre det senere."
              required
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="checkbox"
                checked={isAdmin}
                onChange={(e) => setIsAdmin(e.target.checked)}
                id="admin-checkbox"
              />
              <label htmlFor="admin-checkbox">
                <Typography variant="body2">Giv admin rettigheder</Typography>
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>
            Annuller
          </Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={creating || !newUserEmail || !newUserPassword}
            startIcon={creating ? <CircularProgress size={20} /> : <PersonAddIcon />}
          >
            {creating ? 'Opretter...' : 'Opret bruger'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Undertakers Dialog */}
      <Dialog open={manageDialogOpen} onClose={() => setManageDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Administrer Adgang for {selectedUser?.email}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Tilføj Adgang
            </Typography>
            
            {/* Toggle between chain and location */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button
                variant={claimType === 'location' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setClaimType('location')
                  setSelectedChain(null)
                }}
              >
                Enkelt Lokation
              </Button>
              <Button
                variant={claimType === 'chain' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setClaimType('chain')
                  setSelectedUndertaker(null)
                }}
              >
                Hele Kæde
              </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              {claimType === 'location' ? (
                <Autocomplete
                  fullWidth
                  options={allUndertakers}
                  getOptionLabel={(option) => `${option.name} - ${option.address}, ${option.city}`}
                  value={selectedUndertaker}
                  onChange={(_, newValue) => setSelectedUndertaker(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Vælg lokation" placeholder="Søg navn, adresse eller by..." />
                  )}
                  renderOption={(props, option) => {
                    const isAssigned = userUndertakers.some(ut => ut.undertaker_id === option.id)
                    return (
                      <li {...props} key={option.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          {isAssigned && (
                            <Chip 
                              label="✓" 
                              size="small" 
                              color="success" 
                              sx={{ mr: 1, minWidth: 30 }}
                            />
                          )}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: isAssigned ? 600 : 400 }}>
                              {option.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.address}, {option.city}
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    )
                  }}
                  filterOptions={(options, { inputValue }) => {
                    const searchTerm = inputValue.toLowerCase()
                    return options.filter(
                      option =>
                        option.name.toLowerCase().includes(searchTerm) ||
                        option.city.toLowerCase().includes(searchTerm) ||
                        option.address.toLowerCase().includes(searchTerm)
                    )
                  }}
                />
              ) : (
                <Autocomplete
                  fullWidth
                  options={allChains}
                  getOptionLabel={(option) => option.name}
                  value={selectedChain}
                  onChange={(_, newValue) => setSelectedChain(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Vælg kæde" placeholder="Søg kædenavn..." />
                  )}
                  renderOption={(props, option) => {
                    const isAssigned = userUndertakers.some(ut => ut.chain_id === option.id)
                    return (
                      <li {...props} key={option.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          {isAssigned && (
                            <Chip 
                              label="✓" 
                              size="small" 
                              color="success" 
                              sx={{ mr: 1, minWidth: 30 }}
                            />
                          )}
                          <Typography variant="body2" sx={{ fontWeight: isAssigned ? 600 : 400 }}>
                            {option.name}
                          </Typography>
                        </Box>
                      </li>
                    )
                  }}
                />
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddUndertaker}
                disabled={(claimType === 'location' && !selectedUndertaker) || (claimType === 'chain' && !selectedChain) || managingUndertakers}
                sx={{ minWidth: 120 }}
              >
                Tilføj
              </Button>
            </Box>

            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
              Nuværende Adgang ({userUndertakers.length})
            </Typography>
            {userUndertakers.length === 0 ? (
              <Alert severity="info">Ingen adgang tildelt endnu</Alert>
            ) : (
              <List>
                {userUndertakers.map((ut) => (
                  <ListItem key={ut.id} divider>
                    {ut.claim_type === 'chain' ? (
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label="KÆDE" size="small" color="primary" />
                            {ut.chain_name}
                          </Box>
                        }
                        secondary={`${ut.chain_location_count || 0} lokationer`}
                      />
                    ) : (
                      <ListItemText
                        primary={ut.undertaker_name}
                        secondary={`${ut.undertaker_address || 'Adresse mangler'}, ${ut.undertaker_city}`}
                      />
                    )}
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => handleRemoveUndertaker(ut.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDialogOpen(false)}>Luk</Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Nulstil adgangskode for {resetPasswordUser?.email}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Brugeren vil IKKE modtage en email om denne ændring. Giv den nye adgangskode direkte til brugeren.
            </Alert>
            <TextField
              fullWidth
              label="Ny adgangskode"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="Minimum 6 tegn"
              required
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialogOpen(false)} disabled={resettingPassword}>
            Annuller
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            color="warning"
            disabled={resettingPassword || !newPassword}
            startIcon={resettingPassword ? <CircularProgress size={20} /> : <LockResetIcon />}
          >
            {resettingPassword ? 'Nulstiller...' : 'Nulstil adgangskode'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

